import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment/moment";
import { Transition } from "@headlessui/react";
import Layout from "../../components/layout/Layout";
import useCoupons from "../../utils/hooks/useCoupons";
import DefaultImage from "../../assets/images/coupon-default.svg";
import CouponOptions from "../../components/coupon/CouponsOptions";
import DeleteConfirmationPrompt from "../../components/V2/DeleteConfirmationPrompt";
import couponStatus from "../../CouponStatus";
import Spinner from "../../components/Spinner";

function CouponDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const pathParts = location.pathname.split("/");
  const lastPath = pathParts[pathParts.length - 1];

  const {
    coupons,
    couponPrograms,
    isLoadingCoupons,
    isDeletingCoupon,
    deleteCouponHandler,
    isPausedData,
    pauseCouponHandler,
  } = useCoupons({
    couponProgramID: lastPath,
  });
  const selectedCoupon = couponPrograms?.find((item) => item?.id === lastPath);

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showPauseConfirmation, setShowPauseConfirmation] = useState(false);
  const [showCouponOptions, setShowCouponOptions] = useState(false);

  return (
    <Layout
      header
      headerTitle="Online Offer Details"
      backTo="/promotions?tab=coupons"
    >
      {isLoadingCoupons ? (
        <div className="w-screen flex justify-center items-center h-screen">
          <Spinner />
        </div>
      ) : (
        <div className="relative overflow-y-auto min-h-screen lg:max-w-screen-md container mx-auto scroll-smooth pt-[60px] pb-4 px-4">
          <Transition
            className="fixed -bottom-12 left-0 z-20 w-full"
            show={showCouponOptions}
            enterFrom=" translate-y-0"
            enterTo=" -translate-y-12"
            leaveFrom=" -translate-y-12"
            leaveTo=" translate-y-0"
          >
            <CouponOptions
              status={couponStatus(selectedCoupon).status}
              isPaused={selectedCoupon?.is_paused}
              setShowCouponOptions={setShowCouponOptions}
              setShowDeleteConfirmationPrompt={setShowDeleteConfirmation}
              setShowPauseConfirmationPrompt={setShowPauseConfirmation}
            />
          </Transition>
          <Transition
            className="fixed -bottom-12 left-0 z-20 w-full"
            show={showDeleteConfirmation}
            enterFrom=" translate-y-0"
            enterTo=" -translate-y-12"
            leaveFrom=" -translate-y-12"
            leaveTo=" translate-y-0"
          >
            <DeleteConfirmationPrompt
              action={() => deleteCouponHandler(navigate)}
              text="You are about to remove this online offer. Are you sure?"
              closePrompt={setShowDeleteConfirmation}
              isDeleting={isDeletingCoupon}
            />
          </Transition>
          <Transition
            className="fixed -bottom-12 left-0 z-20 w-full"
            show={showPauseConfirmation}
            enterFrom=" translate-y-0"
            enterTo=" -translate-y-12"
            leaveFrom=" -translate-y-12"
            leaveTo=" translate-y-0"
          >
            <DeleteConfirmationPrompt
              action={() =>
                pauseCouponHandler(selectedCoupon?.is_paused !== true)
              }
              text="You are about to pause this online offer. Are you sure?"
              closePrompt={setShowPauseConfirmation}
              isDeleting={isPausedData}
              buttonText="Yes, Pause"
            />
          </Transition>
          <div className="flex flex-col justify-between items-start border-[1px] border-[#E7E7E7] rounded-[0.75rem] p-[0.75rem] space-y-[0.75rem] w-full mt-10">
            <div className="flex flex-row justify-between items-center w-full">
              <p className="text-[#1A1A1A] text-[0.875rem] font-bold">
                {selectedCoupon?.amount !== 0
                  ? `${selectedCoupon?.amount} off`
                  : `${selectedCoupon?.discount}%`}
              </p>
              <button
                className="flex justify-center items-center w-6 h-6 rounded-full bg-[#EFEDEC]"
                type="button"
                onClick={() => setShowCouponOptions(true)}
              >
                <svg
                  width="2"
                  height="12"
                  viewBox="0 0 2 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1 0C0.45 0 0 0.45 0 1C0 1.55 0.45 2 1 2C1.55 2 2 1.55 2 1C2 0.45 1.55 0 1 0ZM1 10C0.45 10 0 10.45 0 11C0 11.55 0.45 12 1 12C1.55 12 2 11.55 2 11C2 10.45 1.55 10 1 10ZM1 5C0.45 5 0 5.45 0 6C0 6.55 0.45 7 1 7C1.55 7 2 6.55 2 6C2 5.45 1.55 5 1 5Z"
                    fill="#1A1A1A"
                  />
                </svg>
              </button>
            </div>
            {!selectedCoupon?.media_url ? (
              <div className="w-full h-[15.25rem] rounded-[0.5rem] bg-[#F4F4F4]">
                <img
                  src={DefaultImage}
                  alt={selectedCoupon?.title}
                  className="w-full h-full object-contain object-center"
                />
              </div>
            ) : (
              <img
                src={selectedCoupon?.media_url}
                alt={selectedCoupon?.title}
                className="w-full h-[15.25rem] rounded-[0.5rem] object-cover object-center"
              />
            )}
            <p className="leading-[1rem] text-[0.875rem] text-[#566370]">
              {selectedCoupon?.description}
            </p>
            <div className="flex flex-col justify-start items-start w-full space-y-[1rem]">
              <div className="flex flex-row justify-between items-center w-full h-[1.63rem] divide-x-[1px] divide-[#E7E7E7]">
                <div className="flex flex-col justify-start items-start space-y-[0.31rem] px-[0.5rem]">
                  <div className="flex flex-row justify-start items-center space-x-[0.25rem]">
                    <div
                      style={{
                        backgroundColor: couponStatus(selectedCoupon).color,
                      }}
                      className="h-[0.63rem] w-[0.63rem] rounded-full"
                    />
                    <p className="text-[#1A1A1A] text-[0.75rem] font-bold">
                      {couponStatus(selectedCoupon).status}
                    </p>
                  </div>
                  <p className="text-[#1A1A1A] text-[0.75rem]">Status</p>
                </div>
                <div className="flex flex-col justify-start items-start space-y-[0.31rem] px-[0.5rem]">
                  <p className="text-[#1A1A1A] text-[0.75rem] font-bold">
                    {moment(selectedCoupon?.created_at).format("DD/MM/YYYY")}
                  </p>
                  <p className="text-[#1A1A1A] text-[0.75rem]">Created on</p>
                </div>
                <div className="flex flex-col justify-start items-start space-y-[0.31rem] px-[0.5rem]">
                  <p className="text-[#1A1A1A] text-[0.75rem] font-bold">
                    {moment(selectedCoupon?.valid_until).format("DD/MM/YYYY")}
                  </p>
                  <p className="text-[#1A1A1A] text-[0.75rem]">Expires on</p>
                </div>
              </div>
              <div className=" border-t-[1px] border-[#E7E7E7] flex flex-row justify-evenly items-center w-full h-[1.63rem] divide-x-[1px] divide-[#E7E7E7] py-[1.5rem]">
                <div className="flex flex-col justify-start items-start space-y-[0.31rem] px-[0.5rem]">
                  <p className="text-[#1A1A1A] text-[0.75rem] font-bold">
                    {selectedCoupon?.amount !== 0
                      ? `${selectedCoupon?.amount} off`
                      : `${selectedCoupon?.discount}%`}
                  </p>
                  <p className="text-[#1A1A1A] text-[0.75rem]">
                    {selectedCoupon?.amount === 0 ? "Discount" : "Amount off"}
                  </p>
                </div>
                <div className="flex flex-col justify-start items-start space-y-[0.31rem] px-[0.5rem]">
                  <p className="text-[#1A1A1A] text-[0.75rem] font-bold">
                    {selectedCoupon?.quantity}
                  </p>
                  <p className="text-[#1A1A1A] text-[0.75rem]">
                    No of vouchers
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col justify-between items-start border-[1px] border-[#E7E7E7] rounded-[0.75rem] p-[0.75rem] space-y-[0.75rem] w-full my-4">
            <div className="flex flex-row justify-between items-center w-full">
              <p className="text-[#1A1A1A] text-[0.875rem] font-bold">
                Activity
              </p>
            </div>
            <div className="flex flex-col justify-start items-start w-full space-y-[1rem]">
              <div className="flex flex-row justify-between items-center w-full h-[1.63rem] divide-x-[1px] divide-[#E7E7E7]">
                <div className="flex flex-col justify-start items-start space-y-[0.31rem] px-[0.5rem]">
                  <p className="text-[#1A1A1A] text-[0.75rem] font-bold">
                    {(selectedCoupon?.quantity || 0) -
                      (selectedCoupon?.claimed_coupons || 0)}
                  </p>
                  <div className="flex flex-row justify-start items-center space-x-[0.25rem]">
                    <svg
                      width="8"
                      height="9"
                      viewBox="0 0 8 9"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle cx="4" cy="4.5" r="4" fill="#FFAD03" />
                      <g clipPath="url(#clip0_9259_4099)">
                        <path
                          d="M4.4987 3.875C4.33294 3.875 4.17397 3.94085 4.05676 4.05806C3.93955 4.17527 3.8737 4.33424 3.8737 4.5C3.8737 4.66576 3.93955 4.82473 4.05676 4.94194C4.17397 5.05915 4.33294 5.125 4.4987 5.125C4.66446 5.125 4.82343 5.05915 4.94064 4.94194C5.05785 4.82473 5.1237 4.66576 5.1237 4.5C5.1237 4.33424 5.05785 4.17527 4.94064 4.05806C4.82343 3.94085 4.66446 3.875 4.4987 3.875ZM4.4987 5.54167C4.22243 5.54167 3.95748 5.43192 3.76213 5.23657C3.56678 5.04122 3.45703 4.77627 3.45703 4.5C3.45703 4.22373 3.56678 3.95878 3.76213 3.76343C3.95748 3.56808 4.22243 3.45833 4.4987 3.45833C4.77496 3.45833 5.03992 3.56808 5.23527 3.76343C5.43062 3.95878 5.54036 4.22373 5.54036 4.5C5.54036 4.77627 5.43062 5.04122 5.23527 5.23657C5.03992 5.43192 4.77496 5.54167 4.4987 5.54167ZM4.4987 2.9375C3.45703 2.9375 2.56745 3.58542 2.20703 4.5C2.56745 5.41458 3.45703 6.0625 4.4987 6.0625C5.54036 6.0625 6.42995 5.41458 6.79036 4.5C6.42995 3.58542 5.54036 2.9375 4.4987 2.9375Z"
                          fill="white"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_9259_4099">
                          <rect
                            width="5"
                            height="5"
                            fill="white"
                            transform="translate(2 2)"
                          />
                        </clipPath>
                      </defs>
                    </svg>
                    <p className="text-[#1A1A1A] text-[0.75rem]">
                      Vouchers left
                    </p>
                  </div>
                </div>
                <div className="flex flex-col justify-start items-start space-y-[0.31rem] px-[0.5rem]">
                  <p className="text-[#1A1A1A] text-[0.75rem] font-bold">
                    {selectedCoupon?.claimed_coupons || 0}
                  </p>
                  <div className="flex flex-row justify-start items-center space-x-[0.25rem]">
                    <svg
                      width="8"
                      height="9"
                      viewBox="0 0 8 9"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle cx="4" cy="4.5" r="4" fill="#B053F9" />
                      <path
                        d="M2.5 3C2.5 2.86739 2.55268 2.74021 2.64645 2.64645C2.74021 2.55268 2.86739 2.5 3 2.5C3.13261 2.5 3.25979 2.55268 3.35355 2.64645C3.44732 2.74021 3.5 2.86739 3.5 3C3.5 3.13261 3.44732 3.25979 3.35355 3.35355C3.25979 3.44732 3.13261 3.5 3 3.5C2.86739 3.5 2.74021 3.44732 2.64645 3.35355C2.55268 3.25979 2.5 3.13261 2.5 3ZM3 2C2.73478 2 2.48043 2.10536 2.29289 2.29289C2.10536 2.48043 2 2.73478 2 3C2 3.26522 2.10536 3.51957 2.29289 3.70711C2.48043 3.89464 2.73478 4 3 4C3.26522 4 3.51957 3.89464 3.70711 3.70711C3.89464 3.51957 4 3.26522 4 3C4 2.73478 3.89464 2.48043 3.70711 2.29289C3.51957 2.10536 3.26522 2 3 2ZM5 3.25C5 3.1837 5.02634 3.12011 5.07322 3.07322C5.12011 3.02634 5.1837 3 5.25 3C5.3163 3 5.37989 3.02634 5.42678 3.07322C5.47366 3.12011 5.5 3.1837 5.5 3.25C5.5 3.3163 5.47366 3.37989 5.42678 3.42678C5.37989 3.47366 5.3163 3.5 5.25 3.5C5.1837 3.5 5.12011 3.47366 5.07322 3.42678C5.02634 3.37989 5 3.3163 5 3.25ZM5.25 2.5C5.05109 2.5 4.86032 2.57902 4.71967 2.71967C4.57902 2.86032 4.5 3.05109 4.5 3.25C4.5 3.44891 4.57902 3.63968 4.71967 3.78033C4.86032 3.92098 5.05109 4 5.25 4C5.44891 4 5.63968 3.92098 5.78033 3.78033C5.92098 3.63968 6 3.44891 6 3.25C6 3.05109 5.92098 2.86032 5.78033 2.71967C5.63968 2.57902 5.44891 2.5 5.25 2.5ZM1.5 5.125C1.5 4.78 1.78 4.5 2.125 4.5H3.875C4.22 4.5 4.5 4.78 4.5 5.125V5.149C4.49962 5.16722 4.49828 5.18542 4.496 5.2035C4.4916 5.24731 4.48408 5.29076 4.4735 5.3335C4.43998 5.47186 4.37968 5.60232 4.296 5.7175C4.084 6.009 3.689 6.25 3 6.25C2.311 6.25 1.916 6.009 1.704 5.7175C1.59437 5.56615 1.5255 5.38915 1.504 5.2035C1.50199 5.18522 1.50066 5.16688 1.5 5.1485V5.125ZM2 5.138C2.00207 5.16429 2.00625 5.19038 2.0125 5.216C2.026 5.2725 2.0535 5.348 2.1085 5.4235C2.2085 5.5615 2.4385 5.75 3 5.75C3.561 5.75 3.791 5.5615 3.8915 5.4235C3.95202 5.33965 3.98943 5.24137 4 5.1385V5.125C4 5.09185 3.98683 5.06005 3.96339 5.03661C3.93995 5.01317 3.90815 5 3.875 5H2.125C2.09185 5 2.06005 5.01317 2.03661 5.03661C2.01317 5.06005 2 5.09185 2 5.125V5.138ZM5.2495 6C5.09258 6.00156 4.93602 5.98477 4.783 5.95C4.87442 5.80568 4.93927 5.64617 4.9745 5.479C5.052 5.492 5.143 5.5 5.2495 5.5C5.6945 5.5 5.8635 5.364 5.9295 5.2815C5.96619 5.23582 5.99032 5.18136 5.9995 5.1235V5.121C5.99846 5.08855 5.98483 5.05777 5.9615 5.03519C5.93817 5.0126 5.90697 4.99998 5.8745 5H4.993C4.97329 4.82108 4.91068 4.64955 4.8105 4.5H5.875C6.22 4.5 6.5 4.78 6.5 5.125V5.1335C6.49957 5.14905 6.4984 5.16456 6.4965 5.18C6.47665 5.33131 6.41579 5.4743 6.3205 5.5935C6.136 5.8235 5.8055 6 5.25 6H5.2495Z"
                        fill="white"
                      />
                    </svg>
                    <p className="text-[#1A1A1A] text-[0.75rem]">
                      People claimed
                    </p>
                  </div>
                </div>
                <div className="flex flex-col justify-start items-start space-y-[0.31rem] px-[0.5rem]">
                  <p className="text-[#1A1A1A] text-[0.75rem] font-bold">
                    {selectedCoupon?.redeemed_coupons || 0}
                  </p>
                  <div className="flex flex-row justify-start items-center space-x-[0.25rem]">
                    <svg
                      width="8"
                      height="9"
                      viewBox="0 0 8 9"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle cx="4" cy="4.5" r="4" fill="#F04438" />
                      <g clipPath="url(#clip0_9259_4119)">
                        <path
                          d="M2.83464 5.5415V5.95817H6.16797V5.5415H2.83464ZM2.83464 3.24984H3.29297C3.27561 3.21859 3.26429 3.1856 3.25901 3.15088C3.25373 3.11616 3.25116 3.0797 3.2513 3.0415C3.2513 2.86789 3.31207 2.72032 3.43359 2.5988C3.55512 2.47727 3.70269 2.4165 3.8763 2.4165C3.98047 2.4165 4.07686 2.44345 4.16547 2.49734C4.25408 2.55123 4.3313 2.61803 4.39714 2.69775L4.5013 2.83317L4.60547 2.69775C4.66797 2.61442 4.74436 2.54671 4.83464 2.49463C4.92491 2.44255 5.02214 2.4165 5.1263 2.4165C5.29991 2.4165 5.44748 2.47727 5.56901 2.5988C5.69054 2.72032 5.7513 2.86789 5.7513 3.0415C5.7513 3.0797 5.74866 3.11616 5.74339 3.15088C5.73811 3.1856 5.72686 3.21859 5.70964 3.24984H6.16797C6.28255 3.24984 6.38061 3.29067 6.46214 3.37234C6.54366 3.454 6.5845 3.55206 6.58464 3.6665V5.95817C6.58464 6.07275 6.5438 6.17088 6.46214 6.25255C6.38047 6.33421 6.28241 6.37498 6.16797 6.37484H2.83464C2.72005 6.37484 2.62193 6.334 2.54026 6.25234C2.45859 6.17067 2.41783 6.07261 2.41797 5.95817V3.6665C2.41797 3.55192 2.4588 3.4538 2.54047 3.37213C2.62214 3.29046 2.72019 3.2497 2.83464 3.24984ZM2.83464 4.9165H6.16797V3.6665H5.10547L5.54297 4.26025L5.20964 4.49984L4.5013 3.5415L3.79297 4.49984L3.45964 4.26025L3.88672 3.6665H2.83464V4.9165ZM3.8763 3.24984C3.93533 3.24984 3.98484 3.22984 4.02484 3.18984C4.06484 3.14984 4.08477 3.10039 4.08464 3.0415C4.08464 2.98248 4.06464 2.93296 4.02464 2.89296C3.98464 2.85296 3.93519 2.83303 3.8763 2.83317C3.81727 2.83317 3.76776 2.85317 3.72776 2.89317C3.68776 2.93317 3.66783 2.98261 3.66797 3.0415C3.66797 3.10053 3.68797 3.15005 3.72797 3.19005C3.76797 3.23005 3.81741 3.24998 3.8763 3.24984ZM5.1263 3.24984C5.18533 3.24984 5.23484 3.22984 5.27484 3.18984C5.31484 3.14984 5.33477 3.10039 5.33464 3.0415C5.33464 2.98248 5.31464 2.93296 5.27464 2.89296C5.23464 2.85296 5.18519 2.83303 5.1263 2.83317C5.06727 2.83317 5.01776 2.85317 4.97776 2.89317C4.93776 2.93317 4.91783 2.98261 4.91797 3.0415C4.91797 3.10053 4.93797 3.15005 4.97797 3.19005C5.01797 3.23005 5.06741 3.24998 5.1263 3.24984Z"
                          fill="white"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_9259_4119">
                          <rect
                            width="5"
                            height="5"
                            fill="white"
                            transform="translate(2 2)"
                          />
                        </clipPath>
                      </defs>
                    </svg>
                    <p className="text-[#1A1A1A] text-[0.75rem]">
                      People redeemed
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-[1rem] flex w-full flex-col items-start space-y-[1rem] overflow-hidden rounded-[0.75rem] border-[1px] border-[#E7E7E7] px-[1rem] py-[1.25rem]">
            <p className="text-[#1A1A1A] text-[0.875rem] font-bold">
              Redemption history
            </p>
            {!isLoadingCoupons ? (
              <div>
                {coupons && coupons?.length > 0 ? (
                  coupons?.map((h) => (
                    <div className="flex w-full flex-row justify-start space-x-[0.75rem] py-[0.5rem]">
                      <div className="flex h-[2.5rem] w-[2.5rem] shrink-0 items-center justify-center rounded-full bg-[#F8F6FB]">
                        <p className="text-[0.875rem] font-semibold text-[#4B1D8F]">
                          {h.customer_name.charAt(0)}
                        </p>
                      </div>
                      <div className="item-start flex flex-col justify-start space-y-[0.5rem]">
                        <p className="text-[0.875rem] font-semibold text-[#1A1A1A]">
                          {h.claimed_by}
                        </p>
                        <p className="text-[0.75rem]  text-[#566370]">
                          {moment(h.claimed_at).format("DD MMM, YYYY")} at{" "}
                          {moment(h.claimed_at).format("hh:mm")}{" "}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-[0.75rem] text-[#1A1A1A]">
                    No redemption history
                  </p>
                )}
              </div>
            ) : (
              <p className="text-[0.75rem] text-[#1A1A1A]">
                Loading redemption history...
              </p>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}

export default CouponDetails;
